import { formatUrl } from 'url-lib';
import { arrayOf, Schema } from 'normalizr';
import { transformUtil, deepKeysToCamel } from '@eb/transformation-utils';
import {
    URL_PARAM_NAME_SEARCH_QUERY,
    URL_PARAM_NAME_PAGE_NUMBER,
    URL_PARAM_NAME_PAGE_SIZE,
    URL_PARAM_NAME_ORGANIZER_ID,
    DEFAULT_SEARCH_QUERY,
    DEFAULT_PAGE_NUMBER,
    DEFAULT_PAGE_SIZE,
    DEFAULT_STATUS_FILTER,
    DEFAULT_ORGANIZER_ID,
    STATUS_FILTERS,
} from './constants';

/**
 * Appends the status suffix to the specified events base URL
 * @param {string} eventsBasePath - Base URL to the events page
 * @param {string} [status] - Event status filter
 * @returns {string}
 */
export const getEventsStatusUrl = (eventsBasePath, status = '') => {
    let urlPath = eventsBasePath;

    if (status && status !== DEFAULT_STATUS_FILTER) {
        urlPath = `${urlPath}/${status}`;
    }

    return urlPath;
};

const _getEventStatusFromRoute = (eventsBasePath, requestPath) => {
    // Test to see if the request path has a status filter at the end
    const eventsStatusRouteTest = new RegExp(`${eventsBasePath}/(\\w+)`);
    const statusFilterMatch = requestPath.match(eventsStatusRouteTest);

    return statusFilterMatch;
};

/**
 *
 * @param {string} eventsBasePath
 * @param {string} requestPath
 * @returns {Status}
 */
const _parseEventsStatus = (eventsBasePath, requestPath) => {
    /**
     * @type {Status}
     */
    let status = DEFAULT_STATUS_FILTER;
    const statusFilterMatch = _getEventStatusFromRoute(
        eventsBasePath,
        requestPath,
    );

    // If it does, set that as the status_filter
    // But ensure that the status filter is valid otherwise default
    if (statusFilterMatch && STATUS_FILTERS.includes(statusFilterMatch[1])) {
        status = statusFilterMatch[1];
    }

    return status;
};

const _normalizeNumericParamValue = (queryValue, defaultValue) => {
    let value = parseInt(queryValue, 10);

    if (!Number.isInteger(value) || value <= 0) {
        value = defaultValue;
    }

    return value;
};

/**
 * @typedef {'all' | 'published' | 'draft' | 'past' | 'future'} Status
 *
 * @typedef {object} EventParams
 * @property {Status} [status] Event status filter
 * @property {string} [searchQuery] query string
 * @property {string|number} [pageNumber] current pagination page number
 * @property {number} [pageSize] current pagination page size
 * @property {string} [organizerId] current organizer (profile) ID
 */

/**
 * Parses URL parameters into a component readable object to be passed as props, removing unknown parameters
 * @param {string} eventsBasePath Events page base URL
 * @param {string} [requestPath=''] Current request URL
 * @param {object} [queryParams={}] Request query parameters
 * @param {string} [queryParams.q] query string that is being searched
 * @param {string|number} [queryParams.pg] page number of the current pagination pointer
 * @param {string} [queryParams.ps] current page size
 * @param {string} [queryParams.org] current organizer (profile) ID
 * @returns {EventParams}
 */
export const parseEventsParams = (
    eventsBasePath,
    requestPath = '',
    queryParams = {},
) => ({
    // NOTE: This is duplicated in ebapps/organizations/utils.py (_parse_events_params)
    status: _parseEventsStatus(eventsBasePath, requestPath),
    searchQuery:
        queryParams[URL_PARAM_NAME_SEARCH_QUERY] || DEFAULT_SEARCH_QUERY,
    pageNumber: _normalizeNumericParamValue(
        queryParams[URL_PARAM_NAME_PAGE_NUMBER],
        DEFAULT_PAGE_NUMBER,
    ),
    pageSize: _normalizeNumericParamValue(
        queryParams[URL_PARAM_NAME_PAGE_SIZE],
        DEFAULT_PAGE_SIZE,
    ),
    organizerId:
        queryParams[URL_PARAM_NAME_ORGANIZER_ID] || DEFAULT_ORGANIZER_ID,
});

/**
 * Serializes filter params into events path w/ query parameters
 * @param {string} eventsBasePath Events page base URL
 * @param {EventParams} [filterParams] Filter parameters from UI to serialize
 * @returns {string} New events path w/ query parameters
 */
export const serializeEventParamsToUrl = (
    eventsBasePath,
    {
        status = undefined,
        searchQuery = undefined,
        pageNumber = undefined,
        pageSize = undefined,
        organizerId = undefined,
    } = {},
) =>
    formatUrl(getEventsStatusUrl(eventsBasePath, status), {
        // Explicitly setting a value of undefined means the param won't be included in URL
        [URL_PARAM_NAME_SEARCH_QUERY]:
            searchQuery !== DEFAULT_SEARCH_QUERY ? searchQuery : undefined,
        [URL_PARAM_NAME_PAGE_NUMBER]:
            pageNumber !== DEFAULT_PAGE_NUMBER ? pageNumber : undefined,
        [URL_PARAM_NAME_PAGE_SIZE]:
            pageSize !== DEFAULT_PAGE_SIZE ? pageSize : undefined,
        [URL_PARAM_NAME_ORGANIZER_ID]:
            organizerId !== DEFAULT_ORGANIZER_ID ? organizerId : undefined,
    });

/**
 * Derives whether or not the event is visible based on its properties
 * @param {Event} event
 * @returns {boolean}
 */
const getEventVisibility = (event) => {
    if (event.password) {
        return false;
    } else if (event.inviteOnly) {
        return false;
    } else if (event.listed === false) {
        return true;
    }

    return false;
};

const eventSchema = new Schema('events', {
    assignEntity: (event) => {
        const updatedEvent = event;

        if (!updatedEvent.visibility) {
            updatedEvent.visibility = getEventVisibility(updatedEvent);
        }
    },
});

/**
 * @typedef PublishSettings
 * @property {string} [publishedDate]
 * @property {string} [schedulePublishDate]
 * @property {'scheduled' | 'unscheduled'} schedulePublishStatus
 * @property {boolean} canPublish
 *
 * @typedef SalesStatusInfo
 * @property {string} startSalesDate
 * @property {EventSalesMessageType} messageType
 * @property {EventSalesStatus} salesStatus
 * @property {string} message
 * @property {EventSalesMessageCode} messageCode
 *
 * @typedef Event
 * @property {string} id
 * @property {import('./fixtures').MultipartText} name
 * @property {import('./fixtures').MultipartText} description
 * @property {string} url
 * @property {import('./fixtures').DateWithTimezone} start
 * @property {import('./fixtures').DateWithTimezone} end
 * @property {string} organizationId
 * @property {string} organizerId
 * @property {import('./fixtures').EventStatus} status
 * @property {SalesStatusInfo} event_sales_status
 * @property {boolean} listed
 * @property {string} [password]
 * @property {boolean} inviteOnly
 * @property {PublishSettings} publishSettings
 *
 * @typedef NormalizedEvents
 * @property {Object.<string, Event>} [eventEntities] - Events look up by event ID
 * @property {string[]} [eventIds] - Sorted list of event IDs
 */
/**
 * Normalizes an events list from the API into event IDs & entities
 * All of the properties are transformed into camelCase.
 * @param {import('./fixtures').ApiEvent[]} events List of events from the API to normalize
 * @returns {NormalizedEvents}
 */
export const transformEvents = (events) => {
    if (!events) {
        return {};
    }

    const { entities, result } = transformUtil({
        response: events,
        schema: arrayOf(eventSchema),
    });

    return {
        eventEntities: entities.events,
        eventIds: result,
    };
};

const organizersSchema = new Schema('organizers');

/**
 *
 * @typedef Organizer
 * @property {string} id
 * @property {string} name
 * @property {MultipartText} description
 * @property {MultipartText} longDescription
 * @property {string} url
 * @property {string} organizationId
 * @property {string} logoId
 * @property {Number} numPastEvents
 * @property {Number} numFutureEvents
 * @property {string} resourceUri
 *
 * @typedef NormalizedOrganizers
 * @property {Object.<string, Event>} [organizerEntities] - Organizers look up by organizer ID
 * @property {string[]} [organizerIds] - Sorted list of organizer IDs
 */
/**
 * Normalizes a organizers list from the API into organizer IDs & entities.
 * All of the properties are converted into camelCase.
 * @param {import('./fixtures').ApiOrganizer[]} organizers List of organizers to normalize
 * @returns {NormalizedOrganizers}
 */
export const transformOrganizers = (organizers) => {
    if (!organizers) {
        return {};
    }

    const { entities, result } = transformUtil({
        response: organizers,
        schema: arrayOf(organizersSchema),
    });

    return {
        organizerEntities: entities.organizers,
        organizerIds: result,
    };
};

/**
 * Turns all the keys for from snake case to camel case
 * @param {import('./fixtures').ApiStatusCounts{}} statusCounts An object with K/V pairs
 * of the statuses and their counts
 * @return {TransformedStatusCounts}
 */
export const transformStatusCounts = (statusCounts) => {
    if (!statusCounts) {
        return {};
    }

    return deepKeysToCamel(statusCounts);
};
